import React, { useState, useEffect } from 'react';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Paper, Box, Typography, Chip, CircularProgress,
  Alert, Tooltip, Skeleton, IconButton, TextField,
  TableSortLabel, LinearProgress,
  TablePagination
} from '@mui/material';

import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';
import { fetchOrdersFromFirestore } from '../../../services/orderService.firestore';
import userDataService from '../../../services/userDataService';
import { OrderAdapter } from '../../../adapters/OrderAdapter';

const API_URL = 'https://us-central1-nubelytics.cloudfunctions.net/api';

/**
 * Componente OrdersTable - Tabla con las órdenes más recientes
 * 
 * Características:
 * - Lista de órdenes paginada con detalles clave
 * - Indicador visual del estado de cada orden
 * - Ordenamiento por campos relevantes
 * - Visualización de detalles de orden (mock)
 * - Información detallada sobre cada orden
 */
const OrdersTable = ({
  // Modo controlado: si el padre pasa órdenes y estado de carga, la tabla se limita a mostrarlas.
  orders: externalOrders,
  loading: externalLoading = false,
  orderBy: externalOrderBy,
  orderDirection: externalOrderDirection,
  onOrderByChange,
  onOrderClick,
  page: externalPage,
  totalPages: externalTotalPages,
  onPageChange,
  searchQuery,
  onSearchChange,
  // Props legacy para modo autónomo (dashboard general)
  isLoading,
  period = 'all'
}) => {
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [page, setPage] = useState(1);
  // Cantidad de filas por página (20/50/100/150/200)
  const [perPage, setPerPage] = useState(20);
  // Dirección de ordenamiento (asc | desc)
  const [order, setOrder] = useState('desc');
  const [totalPages, setTotalPages] = useState(1);
  const [orderBy, setOrderBy] = useState('date');
  const [totalRows, setTotalRows] = useState(0);

// --- Integración modo controlado ---------------------------------
// Si el padre pasa un array de órdenes, esta tabla actúa en modo
// "controlado": no realiza fetch interno y solo muestra esas órdenes.
const isControlled = Array.isArray(externalOrders);

// Normalizador de orden (conversión a la estructura interna de la tabla)
const normalizeOrder = (o) => {
  if (!o) return null;
  const toDateObj = (val) => {
    if (!val) return null;
    if (typeof val === 'object') {
      if (typeof val.toDate === 'function') return val.toDate();
      if ('seconds' in val) return new Date(val.seconds * 1000);
    }
    return new Date(val);
  };
  const createdAtDate = toDateObj(o.createdAt || o.date);
  const createdIso = createdAtDate && !isNaN(createdAtDate) ? createdAtDate.toISOString() : null;
  const customerName = o.customer?.name || o.customerName || o.customer || '—';
  return {
    id: o.id,
    date: createdIso,
    customer: customerName,
    total: o.totalPrice ?? o.total_price ?? o.total ?? 0,
    items: Array.isArray(o.items) ? o.items.length : (o.itemsCount ?? o.items_count ?? 0),
    units: Array.isArray(o.items)
      ? o.items.reduce((acc, it) => acc + (it.quantity ?? it.qty ?? 1), 0)
      : (o.units ?? o.unitsCount ?? o.units_count ?? 0),
    status: String(o.status ?? o.orderStatus ?? '').toLowerCase(),
    payment: String(o.paymentStatus ?? o.paymentMethod ?? o.payment ?? '').toLowerCase(),
    shipping: String(
      o.shippingStatus ??
      o.shipping_status ??
      o.fulfillmentStatus ??
      o.fulfillment_status ??
      o.shipping ??
      o.deliveryStatus ??
      ''
    ).toLowerCase()
  };
};

// Sincronizar tabla with datos controlados
useEffect(() => {
  if (isControlled) {
    console.log('🔄 OrdersTable modo controlado - Datos recibidos:', externalOrders?.length || 0);
    const mapped = (externalOrders || []).map(normalizeOrder).filter(Boolean);
    console.log('✅ OrdersTable - Órdenes normalizadas:', mapped.length);
    setOrders(mapped);
    setTotalRows(mapped.length);
    setLocalLoading(externalLoading);
    if (externalPage) setPage(externalPage);
    if (externalOrderBy) setOrderBy(externalOrderBy);
    if (externalOrderDirection) setOrder(externalOrderDirection);
  }
}, [isControlled, externalOrders, externalLoading, externalPage, externalOrderBy, externalOrderDirection]);
  
  const handlePerPageChange = (event) => {
    setPage(1); // reset a primera página
    setPerPage(event.target.value);
  };

  const handlePageChange = (event, value) => {
    if (onPageChange) {
      onPageChange(value);
    } else {
      setPage(value);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    if (onOrderByChange) onOrderByChange(property);
  };

  useEffect(() => {
    if (isControlled) {
      // En modo controlado no hacemos fetch interno
      return;
    }
    const fetchData = async () => {
      setLocalLoading(true);
      setError(null);
      
      try {
        // Definir rango de fechas según período
        let startDate;
        let endDate;

        if (period && period !== 'all') {
          const now = new Date();
          switch (period) {
            case 'last_week': {
              const lastWeek = new Date(now);
              lastWeek.setDate(now.getDate() - 7);
              startDate = lastWeek.toISOString().split('T')[0];
              endDate = now.toISOString().split('T')[0];
              break;
            }
            case 'last_month': {
              const lastMonth = new Date(now);
              lastMonth.setMonth(now.getMonth() - 1);
              startDate = lastMonth.toISOString().split('T')[0];
              endDate = now.toISOString().split('T')[0];
              break;
            }
            default: {
              // Si se pasa otro valor, se interpreta como 'all'
              break;
            }
          }
        }
        // Si period es 'all' o undefined, no se aplican filtros de fecha

        // Obtener órdenes reales desde Firestore (SSOT)
        const storeId = userDataService.getStoreId();
        if (!storeId) {
          throw new Error('storeId no disponible');
        }

        const firestoreResult = await fetchOrdersFromFirestore({
          storeId,
          startDate,
          endDate,
          page,
          perPage,
          orderBy: 'createdAt',
          orderDirection: order,
          source: 'orders-table'
        });

        if (firestoreResult && Array.isArray(firestoreResult.orders)) {
          // Normalizar con OrderAdapter para asegurar consistencia de campos
          const normalized = OrderAdapter.normalizeMany(firestoreResult.orders);
          // Mapear al formato que la tabla espera
          const mappedOrders = normalized.map((o) => {
            // Resolver fecha en formato ISO
            let createdIso = o.createdAt || o.created_at || o.date;
            if (createdIso && typeof createdIso === 'object') {
              if (typeof createdIso.toDate === 'function') {
                createdIso = createdIso.toDate().toISOString();
              } else if ('seconds' in createdIso) {
                createdIso = new Date(createdIso.seconds * 1000).toISOString();
              }
            }
            // Obtener nombre de cliente de forma segura para evitar pasar objetos a la UI
            const customerName = (() => {
              if (typeof o.customerName === 'string' && o.customerName.trim()) {
                return o.customerName.trim();
              }
              if (o.customer && typeof o.customer === 'object') {
                const c = o.customer;
                const candidate = (c.name || `${c.firstName || c.first_name || ''} ${c.lastName || c.last_name || ''}`.trim()).trim();
                if (candidate) return candidate;
              }
              return 'Cliente';
            })();
            return {
              id: String(o.orderNumber || o.order_number || o.id || ''),
              date: createdIso,
              customer: customerName,
              total: o.totalPrice || o.total_price || o.total || 0,
              items: Array.isArray(o.items) ? o.items.length : (o.itemsCount || o.items_count || 0),
              units: Array.isArray(o.items)
                ? o.items.reduce((acc, it) => acc + (it.quantity ?? it.qty ?? 1), 0)
                : (o.units || o.unitsCount || o.units_count || 0),
              status: String(o.status || o.orderStatus || '').toLowerCase(),
              payment: String(o.paymentStatus || o.paymentMethod || o.payment || '').toLowerCase(),
              shipping: String(
                o.shippingStatus ||
                o.shipping_status ||
                o.fulfillmentStatus ||
                o.fulfillment_status ||
                o.shipping ||
                o.deliveryStatus ||
                ''
              ).toLowerCase()
            };
          });

          setOrders(mappedOrders);
          setTotalPages(firestoreResult.totalPages || 1);
          setTotalRows(firestoreResult.totalDocs || (firestoreResult.totalPages || 1) * perPage);
        } else {
          throw new Error('La respuesta de Firestore no tiene el formato esperado');
        }
      } catch (err) {
        console.error('Error al cargar órdenes:', err);
        setError('No pudimos cargar las órdenes recientes.');
        setOrders([]);
        setTotalPages(1);
      } finally {
        setLocalLoading(false);
      }
    };

    fetchData();
  }, [period, page, orderBy, order, perPage, isLoading]);

  // Formato para moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Formatear fecha y hora
  const formatDate = (input) => {
    if (!input) return '—';
    let dateObj = input;
    // Manejar Timestamp de Firestore u objetos serializados
    if (typeof input === 'object' && input !== null) {
      if (typeof input.toDate === 'function') {
        dateObj = input.toDate();
      } else if ('seconds' in input) {
        dateObj = new Date(input.seconds * 1000);
      }
    }
    if (!(dateObj instanceof Date)) {
      dateObj = new Date(dateObj);
    }
    if (isNaN(dateObj)) return '—';
    try {
      return format(dateObj, 'dd MMM yyyy, HH:mm', { locale: es });
    } catch (e) {
      console.error('Error formateando fecha:', e);
      return '—';
    }
  };

  // Renderizar chip de estado
  const renderStatusChip = (status) => {
    if (!status) return <Chip label="—" size="small" variant="outlined" />;
    switch(status) {
      case 'open':
      case 'pending':
        return (
          <Tooltip title="Orden pendiente">
            <Chip 
              icon={<HourglassEmptyIcon />} 
              label="Pendiente" 
              size="small" 
              color="warning"
              variant="outlined"
            />
          </Tooltip>
        );
      case 'processing':
        return (
          <Tooltip title="Orden en proceso">
            <Chip 
              icon={<HourglassEmptyIcon />} 
              label="En proceso" 
              size="small" 
              color="info"
              variant="outlined"
            />
          </Tooltip>
        );
      case 'completed':
      case 'closed':
        return (
          <Tooltip title="Orden completada">
            <Chip 
              icon={<CheckCircleIcon />} 
              label="Completado" 
              size="small" 
              color="success"
              variant="outlined"
            />
          </Tooltip>

        );
      case 'shipped':
        return (
          <Tooltip title="Orden enviada">
            <Chip 
              icon={<LocalShippingIcon />} 
              label="Enviado" 
              size="small" 
              color="info"
              variant="outlined"
            />
          </Tooltip>
        );
      case 'cancelled':
        return (
          <Tooltip title="Orden cancelada">
            <Chip 
              icon={<BlockIcon />} 
              label="Cancelado" 
              size="small" 
              color="error"
              variant="outlined"
            />
          </Tooltip>
        );
      default:
        return (
          <Chip 
            label={status} 
            size="small" 
            variant="outlined"
          />
        );
    }
  };

  // Renderizar chip de método de pago con color e icono
  const renderPaymentChip = (methodRaw) => {
    if (!methodRaw) return <Chip label="—" size="small" variant="outlined" />;
    const method = String(methodRaw).toLowerCase();
    switch(method) {
      case 'paid':
        return (
        <Tooltip title="Pago recibido">
          <Chip icon={<CheckCircleIcon />} label="Pagado" color="success" size="small" variant="outlined" />
        </Tooltip>
      );
      case 'pending':
        return (
        <Tooltip title="Pago pendiente">
          <Chip icon={<HourglassEmptyIcon />} label="Pendiente" color="warning" size="small" variant="outlined" />
        </Tooltip>
      );
      case 'refunded':
        return (
        <Tooltip title="Pago reembolsado">
          <Chip icon={<AttachMoneyIcon />} label="Reembolsado" color="default" size="small" variant="outlined" />
        </Tooltip>
      );
      case 'credit_card':
        return (
        <Tooltip title="Tarjeta de crédito">
          <Chip icon={<CreditCardIcon />} label="Tarjeta" color="primary" size="small" variant="outlined" />
        </Tooltip>
      );
      case 'debit_card':
        return (
        <Tooltip title="Tarjeta de débito">
          <Chip icon={<CreditCardIcon />} label="Débito" color="info" size="small" variant="outlined" />
        </Tooltip>
      );
      case 'mercado_pago':
        return (
        <Tooltip title="Mercado Pago">
          <Chip icon={<PaymentsIcon />} label="Mercado Pago" color="info" size="small" variant="outlined" />
        </Tooltip>
      );
      case 'transfer':
      case 'bank_transfer':
        return (
        <Tooltip title="Transferencia bancaria">
          <Chip icon={<AccountBalanceIcon />} label="Transferencia" color="secondary" size="small" variant="outlined" />
        </Tooltip>
      );
      case 'cash':
        return (
        <Tooltip title="Pago en efectivo">
          <Chip icon={<AttachMoneyIcon />} label="Efectivo" color="success" size="small" variant="outlined" />
        </Tooltip>
      );
      default:
        return (
        <Tooltip title={methodRaw}>
          <Chip label={methodRaw} size="small" variant="outlined" />
        </Tooltip>
      );
    }
  };

/*
  // Renderizar chip de envío
  const renderShippingChip = (shippingRaw) => {
    if (!shippingRaw) return <Chip label="—" size="small" variant="outlined" />;

    const shipping = String(shippingRaw).toLowerCase();

    switch (shipping) {
      // Pendiente de preparación
      case 'unpacked':
      case 'un_packaged':
      case 'pending':
      case 'por_empaquetar':
        return (
          <Tooltip title="Por empaquetar">
            <Chip
              icon={<HourglassEmptyIcon />}
              label="Por empaquetar"
              size="small"
              color="warning"
              variant="outlined"

  const shipping = String(shippingRaw).toLowerCase();

  switch (shipping) {
    // Pendiente de preparación
    case 'unpacked':
    case 'un_packaged':
    case 'pending':
    case 'por_empaquetar':
      return (
        <Tooltip title="Por empaquetar">
          <Chip
            icon={<HourglassEmptyIcon />}
            label="Por empaquetar"
            size="small"
            color="warning"
            variant="outlined"
          />
        </Tooltip>
      );
              variant="outlined"
            />
          </Tooltip>
        );

      // Entregado
      case 'delivered':
      case 'entregado':
        return (
          <Tooltip title="Entregado">
            <Chip
              icon={<CheckCircleIcon />}
              label="Entregado"
              size="small"
              color="success"
              variant="outlined"
            />
          </Tooltip>
        );

      // A convenir (sin logística definida)
      case 'a_convenir':
      case 'a convenir':
      case 'por_acordar':
        return (
          <Tooltip title="A convenir con el cliente">
            <Chip
              icon={<LocalShippingIcon />}
              label="A convenir"
              size="small"
              color="default"
              variant="outlined"
            />
          </Tooltip>
        );

      // Devuelto / Cancelado
      case 'returned':
      case 'devuelto':
      case 'cancelled':
      case 'canceled':
        return (
          <Tooltip title="Devuelto / Cancelado">
            <Chip
              icon={<BlockIcon />}
              label="Devuelto / Cancelado"
              size="small"
              color="error"
              variant="outlined"
            />
          </Tooltip>
        );

      default:
        return <Chip label={shippingRaw} size="small" variant="outlined" />;
    }
  };
*/

  // Renderizar chip de envío
  const renderShippingChip = (shippingRaw) => {
    if (!shippingRaw) return <Chip label="—" size="small" variant="outlined" />;

    const shipping = String(shippingRaw).toLowerCase();

    switch (shipping) {
      // Por empaquetar
      case 'unpacked':
      case 'un_packaged':
      case 'pending':
      case 'por_empaquetar':
      case 'waiting_packing':
      case 'waiting_packaging':
      case 'waiting_shipment':
        return (
          <Tooltip title="Por empaquetar">
            <Chip
              icon={<HourglassEmptyIcon />}
              label="Por empaquetar"
              size="small"
              color="warning"
              variant="outlined"
            />
          </Tooltip>
        );

      // Por enviar / Listo para retiro
      case 'packed':
      case 'ready_to_ship':
      case 'ready_for_pickup':
      case 'ready_pickup':
      case 'listo_para_retiro':
      case 'listo_para_enviar':
      case 'por_enviar':
        return (
          <Tooltip title="Por enviar">
            <Chip
              icon={<LocalShippingIcon />}
              label="Por enviar"
              size="small"
              color="info"
              variant="outlined"
            />
          </Tooltip>
        );

      // Enviada / en tránsito
      case 'shipped':
      case 'in_transit':
      case 'en_transito':
      case 'enviado':
      case 'enviada':
      case 'despachado':
      case 'despachada':
        return (
          <Tooltip title="Enviada">
            <Chip
              icon={<LocalShippingIcon />}
              label="Enviada"
              size="small"
              color="info"
              variant="outlined"
            />
          </Tooltip>
        );

      // Entregada
      case 'delivered':
      case 'entregado':
        return (
          <Tooltip title="Entregado">
            <Chip
              icon={<CheckCircleIcon />}
              label="Entregado"
              size="small"
              color="success"
              variant="outlined"
            />
          </Tooltip>
        );

      // Envío a convenir / sin logística definida
      case 'a_convenir':
      case 'a convenir':
      case 'por_acordar':
        return (
          <Tooltip title="A convenir con el cliente">
            <Chip
              icon={<LocalShippingIcon />}
              label="A convenir"
              size="small"
              color="default"
              variant="outlined"
            />
          </Tooltip>
        );

      // Devuelto / Cancelado
      case 'returned':
      case 'devuelto':
      case 'cancelled':
      case 'canceled':
        return (
          <Tooltip title="Devuelto / Cancelado">
            <Chip
              icon={<BlockIcon />}
              label="Devuelto / Cancelado"
              size="small"
              color="error"
              variant="outlined"
            />
          </Tooltip>
        );

      default:
        return <Chip label={shippingRaw} size="small" variant="outlined" />;
    }
  };

  // Texto legible por fallback o export
  const formatPaymentMethod = (method) => {
    switch(method) {
      case 'credit_card': return 'Tarjeta de crédito';
      case 'debit_card': return 'Tarjeta de débito';
      case 'mercado_pago': return 'Mercado Pago';
      case 'transfer': return 'Transferencia';
      case 'cash': return 'Efectivo';
      default: return method;
    }
  };

  // Renderizar texto del método de pago
  const renderPaymentMethod = (method) => {
    return formatPaymentMethod(method);
  };

  // Componente de acciones de paginación con campo "Ir a:"
const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(count / rowsPerPage));
  const [inputValue, setInputValue] = React.useState(page + 1);

  // Actualizar input cuando cambie página externamente
  React.useEffect(() => {
    setInputValue(page + 1);
  }, [page]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const targetPage = Number(inputValue) - 1;
      if (!isNaN(targetPage) && targetPage >= 0 && targetPage < totalPages) {
        onPageChange(e, targetPage);
      }
    }
  };

  const handleBlur = (e) => {
    const targetPage = Number(inputValue) - 1;
    if (!isNaN(targetPage) && targetPage >= 0 && targetPage < totalPages) {
      onPageChange(e, targetPage);
    } else {
      setInputValue(page + 1); // reset invalido
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, ml: { xs: 1, sm: 3 }, whiteSpace: 'nowrap' }}>
      <Typography variant="body2">Ir a:</Typography>
      <TextField
        size="small"
        variant="standard"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        inputProps={{
          'aria-label': 'Número de página',
          style: { width: 40, textAlign: 'center' }
        }}
      />
      <IconButton
        size="small"
        onClick={(e) => onPageChange(e, Math.max(0, page - 1))}
        disabled={page === 0}
        aria-label="Página anterior"
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={(e) => onPageChange(e, Math.min(totalPages - 1, page + 1))}
        disabled={page >= totalPages - 1}
        aria-label="Página siguiente"
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

// Crear cabezal de tabla con soporte para ordenamiento
  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };






// Texto legible por fallback o export






if (localLoading || isLoading) {
  return (
    <Box sx={{ width: '100%' }}>
      {/* Esqueleto de carga para la tabla */}
      <TableContainer component={Paper} elevation={0}>
        <Table aria-label="Cargando órdenes recientes">
          <TableHead>
            <TableRow>
              <TableCell>ID Orden</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="center">Items</TableCell>

              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Pago</TableCell>
              <TableCell align="center">Estado envío</TableCell>

            </TableRow>
          </TableHead>
          <TableBody>
            { [...Array(5)].map((_, index) => (
              <TableRow key={index}>
                <TableCell><Skeleton variant="text" width={70} /></TableCell>
                <TableCell><Skeleton variant="text" width={120} /></TableCell>
                <TableCell><Skeleton variant="text" width={100} /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width={60} /></TableCell>
                <TableCell align="center"><Skeleton variant="text" width={30} /></TableCell>
                <TableCell align="center"><Skeleton variant="rectangular" width={100} height={28} /></TableCell>
                <TableCell align="center"><Skeleton variant="rectangular" width={100} height={28} /></TableCell>
                <TableCell align="center"><Skeleton variant="rectangular" width={100} height={28} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      {localLoading && <LinearProgress sx={{ width: '100%' }} />}
     </TableContainer>
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <Skeleton variant="rectangular" width={200} height={32} />
      </Box>
      <LinearProgress />
    </Box>
  );
}

if (error && !orders.length) {
  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Alert severity="warning">{error}</Alert>
    </Box>
  );
}

return (
  <Box sx={{ width: '100%' }}>
    <TableContainer component={Paper} elevation={0}>
      <Table aria-label="Tabla de órdenes recientes">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'id'}
                direction={orderBy === 'id' ? order : 'asc'}
                onClick={createSortHandler('id')}
              >
                ID Orden
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'date'}
                direction={orderBy === 'date' ? order : 'asc'}
                onClick={createSortHandler('date')}
              >
                Fecha
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'customer'}
                direction={orderBy === 'customer' ? order : 'asc'}
                onClick={createSortHandler('customer')}
              >
                Cliente
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <TableSortLabel
                active={orderBy === 'total'}
                direction={orderBy === 'total' ? order : 'asc'}
                onClick={createSortHandler('total')}
              >
                Total
              </TableSortLabel>
            </TableCell>
            <TableCell align="center">
              <TableSortLabel
                active={orderBy === 'items'}
                direction={orderBy === 'items' ? order : 'asc'}
                onClick={createSortHandler('items')}
              >
                Productos
              </TableSortLabel>
            </TableCell>

            <TableCell align="center">Estado</TableCell>
            <TableCell align="center">Pago</TableCell>
            <TableCell align="center">Estado envío</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(isControlled ? externalLoading : localLoading) ? (
            [...Array(perPage)].map((_, idx) => (
              <TableRow key={`skeleton-${idx}`}>
                {Array.from({ length: 8 }).map((__, cellIdx) => (
                  <TableCell key={cellIdx}>
                    <Skeleton variant="text" width="100%" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            (isControlled ? (externalOrders || []).map(normalizeOrder).filter(Boolean) : orders).map((order) => (
              <TableRow
                key={order.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                hover
              >
                <TableCell component="th" scope="row">
                  <Typography variant="body2" fontWeight={500}>
                    {order.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(order.date)}
                  </Typography>
                </TableCell>
                <TableCell>{order.customer}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="medium">
                    {formatCurrency(order.total)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={order.items} 
                    size="small" 
                    variant="outlined"
                    color="primary"
                  />
                </TableCell>

                <TableCell align="center">
                  {renderStatusChip(order.status)}
                </TableCell>
                <TableCell align="center">
                  {renderPaymentChip(order.payment)}
                </TableCell>
                <TableCell align="center">
                  {renderShippingChip(order.shipping)}
                </TableCell>

              </TableRow>
            )))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Paginación estándar */}
      <TablePagination
        sx={{
          px: 1,
          '.MuiTablePagination-toolbar': {
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: { xs: 0.5, sm: 1 },
          },
          '.MuiTablePagination-displayedRows': {
            ml: { xs: 0, sm: 2 },
          },
          '.MuiTablePagination-actions': {
            ml: { xs: 0, sm: 2 },
          },
        }}
        component="div"
        rowsPerPageOptions={[10, 20, 50, 100]}
        count={totalRows}
        rowsPerPage={perPage}
        page={page - 1}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        ActionsComponent={TablePaginationActions}
        onPageChange={(_e, newPage) => handlePageChange(null, newPage + 1)}
        onRowsPerPageChange={(e) => handlePerPageChange(e)}
      />
    </Box>
  );
};




export default OrdersTable;
